import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Avatar,
} from "@mui/material";
import {
  CheckCircle,
  Add,
  Close,
  Chat,
  CheckBox,
  CheckBoxOutlineBlank,
  Download,
  Search,
  ArrowBack,
  Business,
} from "@mui/icons-material";

import HttpClient from "../../../../helpers/HttpClient";
import { showToast } from "../../../../utils/toast";
import OverlayLoader from "../../../../components/OverlayLoader";

import NumericInput from "../../Components/NumericInput";
import CustomAutocomplete from "../../Components/CustomAutocomplete";
import CourseManagerDialog from "../../Components/CourseManagerDialog";

interface LoteData {
  id: number;
  lote: number;
  fecha: string;
  distribuidor: string;
  cursos: { code: string; name: string; chatEnabled: boolean }[];
  numeroSeriales: number;
  distribuidorData: string;
  meses: number;
  observaciones: string;
}

interface Distribuidor {
  id: string;
  name: string;
}

interface BatchManagementPageProps {
  loteData: LoteData;
  onBack: () => void;
}

export default function BatchManagementPage({
  loteData,
  onBack,
}: BatchManagementPageProps) {
  console.log(loteData);
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [distribuidoresDisponibles, setDistribuidoresDisponibles] = useState(
    []
  );

  const [openLoader, setOpenLoader] = useState(true);
  const [currentLote, setCurrentLote] = useState<LoteData>({...loteData, distribuidorData: JSON.parse(loteData.distribuidorData)});
  const [openCursosDialog, setOpenCursosDialog] = useState(false);
  const [openMesesDialog, setOpenMesesDialog] = useState(false);
  const [openDistribuidorDialog, setOpenDistribuidorDialog] = useState(false);
  const [openObservacionesDialog, setOpenObservacionesDialog] = useState(false);
  const [selectedCursos, setSelectedCursos] = useState(currentLote.cursos);
  const [newDistribuidor, setNewDistribuidor] = useState(
    JSON.parse(loteData.distribuidorData)
  );
  const [tempObservaciones, setTempObservaciones] = useState(
    currentLote.observaciones
  );

  const [tempMeses, setTempMeses] = useState(
    currentLote.meses
  );

  const handleCloseCursosDialog = () => {
    setOpenCursosDialog(false);
  };

  const getDistributors = async () => {
    const result = await HttpClient().get(`/admin/distributor`);
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de distribuidores. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setDistribuidoresDisponibles(result.data);
    setOpenLoader(false);
  };

  const getCourses = async () => {
    const result = await HttpClient().get(`/admin/courses`);
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo cargar información de cursos. Refresca la página e intenta nuevamente.",
        "error"
      );
      return;
    }

    setCursosDisponibles(result.data);
    setOpenLoader(false);
  };

  const handleSaveCursos = () => {
    setCurrentLote((prev) => ({ ...prev, cursos: selectedCursos }));
    setOpenCursosDialog(false);
  };

  const handleSaveMeses = () => {
    setCurrentLote((prev) => ({ ...prev, meses: tempMeses }));
    setOpenMesesDialog(false);
  };

  const handleSaveDistribuidor = () => {
    setCurrentLote((prev) => ({ ...prev, distribuidorData: newDistribuidor }));
    setOpenDistribuidorDialog(false);
  };

  const handleSaveObservaciones = () => {
    setCurrentLote((prev) => ({ ...prev, observaciones: tempObservaciones }));
    setOpenObservacionesDialog(false);
  };

  const handleAddCurso = (curso: { code: string; name: string }) => {
    if (!selectedCursos.find((c) => c.code === curso.code)) {
      setSelectedCursos((prev) => [...prev, { ...curso, chatEnabled: false }]);
    }
  };

  const handleRemoveCurso = (cursoCode: string) => {
    setSelectedCursos((prev) => prev.filter((c) => c.code !== cursoCode));
  };

  const handleToggleChatInModal = (cursoCode: string) => {
    setSelectedCursos((prev) =>
      prev.map((curso) =>
        curso.code === cursoCode
          ? { ...curso, chatEnabled: !curso.chatEnabled }
          : curso
      )
    );
  };

  const handleToggleChatMasivo = (enabled: boolean) => {
    setSelectedCursos((prev) =>
      prev.map((curso) => ({ ...curso, chatEnabled: enabled }))
    );
  };

  const generateFile = (data, name) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const isFormValid = () => {
    const meses = Number.parseInt(currentLote.meses) || 0;
   
    return (
      currentLote.cursos.length > 0 &&
      currentLote.distribuidorData !== null &&
      meses >= 1
    );
  };

  const handleUpdate = async () => {
    console.log(currentLote)
    setOpenLoader(true);
    const result = await HttpClient().put(`/admin/batch/edit`, {
      distributor: currentLote.distribuidorData?.id,
      id: currentLote.id,
      months: currentLote.meses,
      observations: currentLote.observaciones,
      courses: [...new Set(currentLote.cursos.map((ele) => ele.code))].join(","),
      selectedCoursesData: currentLote.cursos
    });
    console.log(result)
    if (result.error) {
      setOpenLoader(false);
      showToast(
        "No se pudo guardar el lote. Intenta nuevamente.",
        "error"
      );
      return;
    }
    setOpenLoader(false);
    if (result.status == 201){
      showToast(
        "El lote fue actualizado correctamente.",
        "success"
      );
      onBack();
    } else {
      showToast(
        "No se actualizó el lote. Intenta nuevamente. Si el error persiste contacta al soporte.",
        "warning"
      );
    }
  }

  const handleExportTXT = async () => {
    const response = await HttpClient().get(
      `/admin/batch/codes/txt/${loteData.id}`,
      {
        responseType: "blob",
      }
    );
    if (response.error) {
      showToast(
        "No se pudo cargar datos. Refresca la página e intenta nuevamente.",
        "error"
      );
    }

    generateFile(response.data, "codigos.txt");
  };

  const handleExportCSV = async () => {
    // Simular exportación CSV
    const response = await HttpClient().get(
      `/admin/batch/codes/csv/${loteData.id}`,
      {
        responseType: "blob",
      }
    );
    if (response.error) {
      showToast(
        "No se pudo cargar datos. Refresca la página e intenta nuevamente.",
        "error"
      );
    }

    generateFile(response.data, "codigos.csv");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    getDistributors();
    getCourses();
  }, []);

  const cursosDisponiblesParaAgregar = cursosDisponibles.filter(
    (curso) => !selectedCursos.find((c) => c.code === curso.code)
  );

  return (
    <Box>
      {/* Header con botón de regreso */}
      <OverlayLoader open={openLoader} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Button
          startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
          onClick={onBack}
          size="small"
          sx={{
            color: "#6B7280",
            "&:hover": { backgroundColor: "#F3F4F6" },
            fontSize: "0.8rem",
          }}
        >
          Volver a Lista
        </Button>
        {/*<Typography variant="h5" fontWeight="bold" sx={{ fontSize: "1.3rem" }}>
          Gestión de lotes online
        </Typography>*/}
      </Box>

      <Card sx={{ borderRadius: 2, border: "1px solid #E5E7EB" }}>
        <CardContent sx={{ p: 2 }}>
          {/* Header con estado */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CheckCircle sx={{ color: "#10B981", fontSize: 20 }} />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: "0.8rem" }}
            >
              Lote seleccionado del sistema
            </Typography>
          </Box>

          {/* Información principal en grid */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Lote y Fecha */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ minWidth: 50, fontSize: "0.9rem" }}
                >
                  Lote:
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#B0BEC5",
                    color: "white",
                    px: 2,
                    py: 0.8,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    minWidth: 60,
                    textAlign: "center",
                  }}
                >
                  {currentLote.lote}
                </Box>
                {/*<Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#64748B",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Search sx={{ color: "white", fontSize: 16 }} />
                </Box>*/}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ minWidth: 140, fontSize: "0.9rem" }}
                >
                  Fecha del registro:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ fontSize: "0.8rem" }}
                >
                  {formatDate(currentLote.fecha)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Distribuidor */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ mb: 1.5, fontSize: "0.9rem" }}
            >
              Distribuidor
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={currentLote.distribuidorData?.name || "Sin especificar"}
              variant="outlined"
              disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F9FAFB",
                  fontSize: "0.8rem",
                  "& .Mui-disabled": {
                    color: "#374151",
                    "-webkit-text-fill-color": "#374151",
                  },
                },
              }}
            />
          </Box>

          {/* Cursos */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ fontSize: "0.9rem" }}
              >
                Curso(s) Activos: ({currentLote.cursos.length})
              </Typography>
              {currentLote.cursos.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CheckBoxOutlineBlank sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      setCurrentLote((prev) => ({
                        ...prev,
                        cursos: prev.cursos.map((curso) => ({
                          ...curso,
                          chatEnabled: false,
                        })),
                      }));
                    }}
                    sx={{ fontSize: "0.7rem", py: 0.3 }}
                  >
                    Desactivar Chat Todos
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckBox sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      setCurrentLote((prev) => ({
                        ...prev,
                        cursos: prev.cursos.map((curso) => ({
                          ...curso,
                          chatEnabled: true,
                        })),
                      }));
                    }}
                    sx={{
                      fontSize: "0.7rem",
                      py: 0.3,
                      backgroundColor: "#10B981",
                      "&:hover": { backgroundColor: "#059669" },
                    }}
                  >
                    Activar Chat Todos
                  </Button>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 1.5,
                p: 1,
                backgroundColor: "#F9FAFB",
                minHeight: 80,
                maxHeight: 200,
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#F3F4F6",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#D1D5DB",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "#9CA3AF",
                  },
                },
              }}
            >
              {currentLote.cursos.map((curso) => (
                <Box
                  key={curso.code}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    p: 1,
                    backgroundColor: "white",
                    borderRadius: 1.5,
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      label={curso.code}
                      size="small"
                      sx={{
                        backgroundColor: "#10B981",
                        color: "white",
                        fontWeight: "bold",
                        mr: 0.8,
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {curso.name}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={curso.chatEnabled}
                          onChange={() => {
                            setCurrentLote((prev) => ({
                              ...prev,
                              cursos: prev.cursos.map((c) =>
                                c.code === curso.code
                                  ? { ...c, chatEnabled: !c.chatEnabled }
                                  : c
                              ),
                            }));
                          }}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#10B981",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#10B981",
                              },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          <Chat
                            sx={{
                              fontSize: 14,
                              color: curso.chatEnabled ? "#10B981" : "#9CA3AF",
                            }}
                          />
                          <Typography
                            variant="caption"
                            color={
                              curso.chatEnabled ? "#10B981" : "textSecondary"
                            }
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Chat
                          </Typography>
                        </Box>
                      }
                      labelPlacement="start"
                      sx={{ m: 0 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Observaciones */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ mb: 1.5, fontSize: "0.9rem" }}
            >
              Observaciones:
            </Typography>
            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 1.5,
                p: 1,
                backgroundColor: "#B0BEC5",
                minHeight: 50,
              }}
            >
              <Typography
                variant="body2"
                color="white"
                sx={{ fontSize: "0.8rem" }}
              >
                {currentLote.observaciones || "Sin observaciones"}
              </Typography>
            </Box>
          </Box>

          {/* Botones de acción principales */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setSelectedCursos(currentLote.cursos);
                setOpenCursosDialog(true);
              }}
              sx={{
                backgroundColor: "#6B7280",
                "&:hover": { backgroundColor: "#4B5563" },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Cambiar Cursos
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setTempMeses(currentLote.meses);
                setOpenMesesDialog(true);
              }}
              sx={{
                backgroundColor: "#6B7280",
                "&:hover": { backgroundColor: "#4B5563" },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Cambiar # meses
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setNewDistribuidor(currentLote.distribuidor);
                setOpenDistribuidorDialog(true);
              }}
              sx={{
                backgroundColor: "#6B7280",
                "&:hover": { backgroundColor: "#4B5563" },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Cambiar Distribuidor
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Botones de acción secundarios */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              startIcon={<Search sx={{ fontSize: 14 }} />}
              size="small"
              onClick={() => {
                setTempObservaciones(currentLote.observaciones);
                setOpenObservacionesDialog(true);
              }}
              sx={{
                backgroundColor: "#64748B",
                color: "white",
                "&:hover": { backgroundColor: "#475569" },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Observaciones
            </Button>
            <Button
              startIcon={<Download sx={{ fontSize: 14 }} />}
              size="small"
              onClick={handleExportTXT}
              sx={{
                backgroundColor: "#3B82F6",
                color: "white",
                "&:hover": { backgroundColor: "#2563EB" },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Exportar TXT
            </Button>
            <Button
              startIcon={<Download sx={{ fontSize: 14 }} />}
              size="small"
              onClick={handleExportCSV}
              sx={{
                backgroundColor: "#10B981",
                color: "white",
                "&:hover": { backgroundColor: "#059669" },
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              Exportar CSV
            </Button>
          </Box>
          {(selectedCursos.length > 0 || currentLote.distribuidorData) && (
            <Card
              sx={{
                mt: 2,
                backgroundColor: "#F0F9FF",
                border: "1px solid #BAE6FD",
              }}
            >
              <CardContent sx={{ p: 1.5 }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="#0369A1"
                  gutterBottom
                  sx={{ fontSize: "0.75rem" }}
                >
                  Resumen del lote:
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Distribuidor:{" "}
                  {currentLote.distribuidorData?.name || "Sin especificar"}
                  {currentLote.distribuidorData && (
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.65rem", opacity: 0.8 }}
                    >
                      {" "}
                      (ID: {currentLote.distribuidorData.id})
                    </Typography>
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Cursos: {currentLote.cursos.length} seleccionados
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Cursos con chat:{" "}
                  {currentLote.cursos.filter((c) => c.chatEnabled).length}
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Duración: {currentLote.meses || "0"} meses
                </Typography>
                <Typography
                  variant="caption"
                  color="#0369A1"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  • Cantidad: {loteData.numeroSeriales || "0"} seriales
                </Typography>
              </CardContent>
            </Card>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleUpdate}
              disabled={!isFormValid()}
              sx={{
                marginTop: 4,
                backgroundColor: "#9CA3AF",
                color: "white",
                fontWeight: "bold",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: "0.8rem",
                "&:hover": {
                  backgroundColor: "#6B7280",
                },
                "&:disabled": {
                  backgroundColor: "#D1D5DB",
                  color: "#9CA3AF",
                },
              }}
            >
              Actualizar
            </Button>
          </Box>
        </CardContent>
      </Card>

      <CourseManagerDialog
        open={openCursosDialog}
        onClose={handleCloseCursosDialog}
        onSave={handleSaveCursos}
        selectedCourses={selectedCursos}
        availableCourses={cursosDisponiblesParaAgregar}
        onToggleChatAll={handleToggleChatMasivo}
        onToggleChat={handleToggleChatInModal}
        onRemoveCourse={handleRemoveCurso}
        onAddCourse={handleAddCurso}
      />

      {/* Dialog para cambiar meses */}
      <Dialog
        open={openMesesDialog}
        onClose={() => setOpenMesesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Cambiar Número de Meses
        </DialogTitle>
        <DialogContent>
          <NumericInput
            label={"Número de meses:"}
            placeholder={"Ingrese número de meses"}
            sufixText={"meses"}
            helperText="Mínimo 1 mes, máximo 999 meses"
            value={tempMeses}
            min={1}
            max={999}
            setter={(value) => {
              setTempMeses(value)
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMesesDialog(false)} size="small">
            Cancelar
          </Button>
          <Button
            onClick={handleSaveMeses}
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#10B981" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para cambiar distribuidor */}
      <Dialog
        open={openDistribuidorDialog}
        onClose={() => setOpenDistribuidorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Cambiar Distribuidor
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <CustomAutocomplete
              options={distribuidoresDisponibles}
              value={currentLote.distribuidorData}
              onChange={(event, newValue) => {
                console.log(newValue)
                setNewDistribuidor(newValue);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Avatar sx={{ width: 28, height: 28, backgroundColor: "#6B7280" }}>
              <Business sx={{ fontSize: 14 }} />
            </Avatar>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDistribuidorDialog(false)} size="small">
            Cancelar
          </Button>
          <Button
            onClick={handleSaveDistribuidor}
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#10B981" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para observaciones */}
      <Dialog
        open={openObservacionesDialog}
        onClose={() => setOpenObservacionesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Gestionar Observaciones
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Observaciones"
            fullWidth
            multiline
            rows={4}
            size="small"
            variant="outlined"
            value={tempObservaciones}
            onChange={(e) => setTempObservaciones(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenObservacionesDialog(false)}
            size="small"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveObservaciones}
            variant="contained"
            size="small"
            sx={{ backgroundColor: "#10B981" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
